import React, { useState } from "react";
import { usePlayer } from "@/context/PlayerContext";
import { SONGS } from "@/data/eiffel65";
import { resolveSongCover, ALBUM_COVERS } from "@/data/covers";
import {
  Play, Pause, Square, SkipForward, SkipBack, Shuffle, Repeat, Heart, Volume2, VolumeX, ListMusic, X, Maximize2,
} from "lucide-react";
import WinampPanel from "@/components/WinampPanel";

export default function GlobalPlayer() {
  const [winampOpen, setWinampOpen] = useState(false);
  const {
    currentSong, currentSongId,
    isPlaying, currentTime, duration, volume, muted,
    togglePlay, next, prev, stop, seek, setVolume, toggleMute,
    shuffle, setShuffle, repeat, setRepeat,
    playSong,
  } = usePlayer();
  const [liked, setLiked] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);
  const [volOpen, setVolOpen] = useState(false);

  if (!currentSong) return null;

  const officialCover = resolveSongCover(currentSong, ALBUM_COVERS);
  const coverImg = officialCover?.src || (currentSong.youtubeId
    ? `https://i.ytimg.com/vi/${currentSong.youtubeId}/mqdefault.jpg`
    : currentSong.cover);

  const dur = Math.max(0, duration || 0);
  const elapsed = Math.max(0, Math.min(dur || 1, currentTime || 0));
  const remain = Math.max(0, dur - elapsed);
  const progress = dur > 0 ? (elapsed / dur) * 100 : 0;
  const fmt = (s) => `${Math.floor((s || 0) / 60).toString().padStart(2, "0")}:${Math.floor((s || 0) % 60).toString().padStart(2, "0")}`;

  return (
    <div
      data-testid="global-player"
      className="fixed bottom-0 left-0 right-0 z-50 glass-strong border-t border-white/10 shadow-[0_-12px_40px_rgba(0,175,255,0.12)]"
    >
      <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
        <div className="scanline" />
      </div>

      {/* Mobile progress bar on top */}
      <div className="sm:hidden absolute top-px left-0 right-0 h-0.5 bg-white/5">
        <div className="h-full bg-[#00AFFF] shadow-[0_0_8px_#00AFFF] transition-[width] duration-200" style={{ width: `${progress}%` }} />
      </div>

      <div className="container mx-auto max-w-[1500px] px-3 sm:px-6 py-2.5 sm:py-3 grid grid-cols-12 items-center gap-2 sm:gap-5">
        <div className="col-span-6 sm:col-span-3 flex items-center gap-2 sm:gap-3 min-w-0">
          <img src={coverImg} alt={currentSong.title} className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover border border-white/10 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-display font-bold text-white text-xs sm:text-sm truncate">{currentSong.title}</div>
            <div className="text-[10px] sm:text-xs text-white/55 truncate">Eiffel 65 · {currentSong.album}</div>
          </div>
          <button
            data-testid="player-like"
            onClick={() => setLiked((v) => !v)}
            aria-label="Like"
            className={`w-9 h-9 grid place-items-center rounded-full transition-all hidden sm:grid ${
              liked ? "text-[#FF3B6B]" : "text-white/40 hover:text-[#FF3B6B] hover:bg-white/5"
            }`}
          >
            <Heart size={16} fill={liked ? "currentColor" : "none"} />
          </button>
        </div>

        <div className="col-span-4 sm:col-span-6 flex flex-col items-center gap-1 sm:gap-1.5">
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              data-testid="player-shuffle"
              onClick={() => setShuffle(!shuffle)}
              aria-label="Shuffle"
              className={`hidden sm:grid w-9 h-9 place-items-center rounded-full transition-all ${
                shuffle ? "text-[#00AFFF] bg-[#00AFFF]/10" : "text-white/55 hover:text-[#00AFFF] hover:bg-[#00AFFF]/10"
              }`}
            >
              <Shuffle size={14} />
            </button>
            <button
              data-testid="player-prev"
              onClick={prev}
              aria-label="Previous"
              className="w-10 h-10 sm:w-9 sm:h-9 grid place-items-center rounded-full text-white/70 hover:text-[#00AFFF] hover:bg-[#00AFFF]/10 transition-all"
            >
              <SkipBack size={16} />
            </button>
            <button
              data-testid="player-play"
              onClick={togglePlay}
              aria-label={isPlaying ? "Pause" : "Play"}
              className="w-11 h-11 sm:w-12 sm:h-12 grid place-items-center rounded-full bg-[#00AFFF] hover:bg-[#4DC3FF] text-[#020B1F] shadow-[0_0_24px_rgba(0,175,255,0.6)] hover:scale-105 transition-all"
            >
              {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
            </button>
            <button
              data-testid="player-stop"
              onClick={() => { seek(0); }}
              aria-label="Restart"
              className="hidden md:grid w-9 h-9 place-items-center rounded-full text-white/55 hover:text-[#00AFFF] hover:bg-[#00AFFF]/10 transition-all"
            >
              <Square size={14} fill="currentColor" />
            </button>
            <button
              data-testid="player-next"
              onClick={next}
              aria-label="Next"
              className="w-10 h-10 sm:w-9 sm:h-9 grid place-items-center rounded-full text-white/70 hover:text-[#00AFFF] hover:bg-[#00AFFF]/10 transition-all"
            >
              <SkipForward size={16} />
            </button>
            <button
              data-testid="player-repeat"
              onClick={() => setRepeat(!repeat)}
              aria-label="Repeat"
              className={`hidden sm:grid w-9 h-9 place-items-center rounded-full transition-all ${
                repeat ? "text-[#00AFFF] bg-[#00AFFF]/10" : "text-white/55 hover:text-[#00AFFF] hover:bg-[#00AFFF]/10"
              }`}
            >
              <Repeat size={14} />
            </button>
          </div>

          <div className="hidden sm:flex items-center gap-3 w-full max-w-xl">
            <span className="text-[10px] tabular-nums text-white/50 font-mono w-10 text-right" data-testid="player-time-elapsed">{fmt(elapsed)}</span>
            <div
              className="relative flex-1 h-1.5 rounded-full bg-white/10 cursor-pointer group"
              onClick={(e) => {
                if (!dur) return;
                const r = e.currentTarget.getBoundingClientRect();
                const pct = (e.clientX - r.left) / r.width;
                seek(Math.max(0, Math.min(1, pct)) * dur);
              }}
              data-testid="player-progress"
            >
              <div
                className="absolute top-0 left-0 bottom-0 rounded-full bg-[#00AFFF] shadow-[0_0_8px_#00AFFF]"
                style={{ width: `${progress}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white border-2 border-[#00AFFF] shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `calc(${progress}% - 6px)` }}
              />
            </div>
            <span className="text-[10px] tabular-nums text-white/50 font-mono w-10" data-testid="player-time-remaining">-{fmt(remain)}</span>
            <span className="text-[10px] tabular-nums text-white/35 font-mono w-10 text-right hidden md:inline" data-testid="player-time-total">{fmt(dur)}</span>
          </div>
        </div>

        <div className="hidden sm:flex col-span-3 items-center justify-end gap-1.5 relative">
          {/* Winamp mode toggle */}
          <button
            data-testid="player-winamp-toggle"
            aria-label="Toggle Winamp mode"
            onClick={() => setWinampOpen((v) => !v)}
            className={`w-9 h-9 grid place-items-center rounded-full transition-all ${winampOpen ? "text-[#7CFFCB] bg-[#7CFFCB]/10 shadow-[0_0_10px_rgba(124,255,203,0.45)]" : "text-white/55 hover:text-[#7CFFCB] hover:bg-[#7CFFCB]/10"}`}
            title="Winamp mode"
          >
            <Maximize2 size={14}/>
          </button>

          {/* Queue */}
          <button
            data-testid="player-queue"
            aria-label="Queue"
            onClick={() => { setQueueOpen(v => !v); setVolOpen(false); }}
            className={`w-9 h-9 grid place-items-center rounded-full transition-all ${queueOpen ? "text-[#00AFFF] bg-[#00AFFF]/10" : "text-white/55 hover:text-[#00AFFF] hover:bg-[#00AFFF]/10"}`}
          >
            <ListMusic size={15} />
          </button>

          {/* Volume */}
          <div className="relative">
            <button
              data-testid="player-volume"
              aria-label="Volume"
              onClick={() => { setVolOpen(v => !v); setQueueOpen(false); }}
              className={`w-9 h-9 grid place-items-center rounded-full transition-all ${volOpen ? "text-[#00AFFF] bg-[#00AFFF]/10" : "text-white/55 hover:text-[#00AFFF] hover:bg-[#00AFFF]/10"}`}
            >
              {muted || volume === 0 ? <VolumeX size={15} /> : <Volume2 size={15} />}
            </button>
            {volOpen && (
              <div className="absolute bottom-12 right-0 glass-strong rounded-xl p-4 w-56 border border-[#00AFFF]/30 shadow-2xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#00AFFF] font-bold">Volume</span>
                  <button onClick={toggleMute} data-testid="player-mute" className="text-white/55 hover:text-[#00AFFF]">
                    {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
                  </button>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={muted ? 0 : volume}
                  onChange={(e) => setVolume(Number(e.target.value))}
                  data-testid="player-volume-slider"
                  aria-label="Volume slider"
                  className="w-full accent-[#00AFFF] cursor-pointer"
                />
                <div className="text-right text-[10px] font-mono text-white/60 mt-1">{muted ? 0 : volume}%</div>
              </div>
            )}
          </div>

          <button
            data-testid="player-close"
            onClick={stop}
            aria-label="Close"
            className="w-9 h-9 grid place-items-center rounded-full text-white/45 hover:text-red-400 hover:bg-red-500/10 transition-all ml-1"
          >
            <X size={15} />
          </button>

          {/* Queue popover */}
          {queueOpen && (
            <div className="absolute bottom-12 right-0 glass-strong rounded-xl p-3 w-80 border border-[#00AFFF]/30 shadow-2xl">
              <div className="flex items-center justify-between mb-2 px-1">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#00AFFF] font-bold">Up Next · Queue</span>
                <span className="text-[10px] text-white/40">{SONGS.length} tracks</span>
              </div>
              <div className="max-h-72 overflow-y-auto pr-1 space-y-0.5">
                {SONGS.map((s) => {
                  const officialCov = resolveSongCover(s, ALBUM_COVERS);
                  const cov = officialCov?.src || (s.youtubeId ? `https://i.ytimg.com/vi/${s.youtubeId}/mqdefault.jpg` : s.cover);
                  const isCurrent = s.id === currentSongId;
                  return (
                    <button
                      key={s.id}
                      data-testid={`queue-item-${s.id}`}
                      onClick={() => playSong(s.id)}
                      className={`w-full flex items-center gap-2.5 p-2 rounded-lg text-left transition-all ${isCurrent ? "bg-[#00AFFF]/15 border border-[#00AFFF]/40" : "hover:bg-white/5 border border-transparent"}`}
                    >
                      <img src={cov} alt={s.title} className="w-9 h-9 rounded object-cover border border-white/10 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className={`text-xs font-display font-bold truncate ${isCurrent ? "text-[#00AFFF]" : "text-white"}`}>{s.title}</div>
                        <div className="text-[10px] text-white/45 truncate">{s.year} · {s.album}</div>
                      </div>
                      {isCurrent && (
                        <div className="flex items-end gap-[2px] h-3">
                          {[0,1,2].map(i => <span key={i} className="viz-bar" style={{ animationDelay: `${i*0.15}s`, height: 10, width: 2 }} />)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="col-span-2 sm:hidden flex items-center justify-end gap-1">
          <button
            data-testid="player-winamp-toggle-mobile"
            onClick={() => setWinampOpen((v) => !v)}
            aria-label="Toggle Winamp mode"
            className={`w-10 h-10 grid place-items-center rounded-full transition-all ${winampOpen ? "text-[#7CFFCB] bg-[#7CFFCB]/10" : "text-white/55 hover:text-[#7CFFCB]"}`}
          >
            <Maximize2 size={16}/>
          </button>
          <button
            data-testid="player-close-mobile"
            onClick={stop}
            aria-label="Close player"
            className="w-10 h-10 grid place-items-center rounded-full text-white/55 hover:text-red-400 active:bg-white/10 transition-all"
          >
            <X size={18} />
          </button>
        </div>
      </div>
      <WinampPanel open={winampOpen} onClose={() => setWinampOpen(false)}/>
    </div>
  );
}
